import { Link } from 'react-router-dom';
import {
  Mail,
  Send,
  Calendar,
  Clock,
  BookOpen,
  User,
  Settings,
  Wrench,
  ChevronRight,
} from 'lucide-react';
import type { AdminWorldDetail, AdminWorldState, AdminWorldEvolution } from '../../../lib/api';
import type { WorldSection } from './WorldSidebar';

interface WorldOverviewProps {
  world: AdminWorldDetail;
  state: AdminWorldState | null;
  evolutions: AdminWorldEvolution[];
  onNavigate: (section: WorldSection) => void;
}

export function WorldOverview({ world, state, evolutions, onNavigate }: WorldOverviewProps) {
  const canon = state?.canon as {
    entities?: {
      characters?: Record<string, unknown>;
      places?: Record<string, unknown>;
      factions?: Record<string, unknown>;
      items?: Record<string, unknown>;
    };
    relationships?: unknown[];
    timeline?: { events?: Record<string, unknown> };
    threads?: {
      open?: Record<string, unknown>;
      closed?: Record<string, unknown>;
    };
  } | null;

  const entityCount = canon
    ? Object.keys(canon.entities?.characters ?? {}).length +
      Object.keys(canon.entities?.places ?? {}).length +
      Object.keys(canon.entities?.factions ?? {}).length +
      Object.keys(canon.entities?.items ?? {}).length
    : 0;

  const relationshipCount = Array.isArray(canon?.relationships) ? canon.relationships.length : 0;
  const eventCount = Object.keys(canon?.timeline?.events ?? {}).length;
  const threadCount = Object.keys(canon?.threads?.open ?? {}).length + Object.keys(canon?.threads?.closed ?? {}).length;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="font-display text-xl text-parchment">World Overview</h1>
        <p className="text-mist text-sm mt-1">{world.inboundAddress}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Mail}
          label="Letters In"
          value={world.recentLettersIn.length.toString()}
          sublabel={world.lastInboundAt ? `Last: ${formatDateShort(world.lastInboundAt)}` : 'None yet'}
        />
        <StatCard
          icon={Send}
          label="Letters Out"
          value={world.recentLettersOut.length.toString()}
          sublabel={state ? `State v${state.version}` : 'No state'}
        />
        <StatCard
          icon={Calendar}
          label="Day Index"
          value={world.dayIndex.toString()}
          sublabel={`Created ${formatDateShort(world.createdAt)}`}
        />
        <StatCard
          icon={Clock}
          label="Delivery"
          value={world.deliveryTime}
          sublabel={world.timezone}
        />
      </div>

      {/* Canon Summary (if state exists) */}
      {state && (
        <div className="bg-night border border-stone/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg text-parchment">World State</h2>
            <button
              onClick={() => onNavigate('canon')}
              className="text-rune hover:underline text-sm flex items-center gap-1"
            >
              View Canon <ChevronRight size={14} />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MiniStat label="Entities" value={entityCount} />
            <MiniStat label="Relationships" value={relationshipCount} />
            <MiniStat label="Events" value={eventCount} />
            <MiniStat label="Threads" value={threadCount} />
          </div>
        </div>
      )}

      {/* Quick Navigation */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <NavCard
          icon={BookOpen}
          title="Canon"
          description="Entities, relationships, timeline, threads"
          onClick={() => onNavigate('canon')}
          disabled={!state}
        />
        <NavCard
          icon={User}
          title="Correspondent"
          description="Character profile and inner dialog"
          onClick={() => onNavigate('correspondent')}
          disabled={!state}
        />
        <NavCard
          icon={Settings}
          title="Configuration"
          description="Style, LLM, and delivery settings"
          onClick={() => onNavigate('configuration')}
        />
        <NavCard
          icon={Wrench}
          title="Operations"
          description="Generate, evolve, compact, and more"
          onClick={() => onNavigate('operations')}
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Inbound */}
        <div className="bg-night border border-stone/30 rounded-xl p-4">
          <h3 className="font-display text-base text-parchment mb-3">Recent Inbound</h3>
          {world.recentLettersIn.length === 0 ? (
            <div className="text-mist text-sm py-4 text-center">No letters received</div>
          ) : (
            <div className="space-y-2">
              {world.recentLettersIn.slice(0, 5).map((letter) => (
                <Link
                  key={letter.id}
                  to={`/admin/letters-in/${letter.id}`}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-stone/20 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-parchment text-sm truncate">
                      {letter.subject ?? '(No subject)'}
                    </div>
                    <div className="text-mist text-xs">{letter.fromEmail}</div>
                  </div>
                  <StatusBadge status={letter.status} />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Outbound */}
        <div className="bg-night border border-stone/30 rounded-xl p-4">
          <h3 className="font-display text-base text-parchment mb-3">Recent Outbound</h3>
          {world.recentLettersOut.length === 0 ? (
            <div className="text-mist text-sm py-4 text-center">No letters sent</div>
          ) : (
            <div className="space-y-2">
              {world.recentLettersOut.slice(0, 5).map((letter) => (
                <Link
                  key={letter.id}
                  to={`/admin/letters-out/${letter.id}`}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-stone/20 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-parchment text-sm truncate">{letter.subject}</div>
                    <div className="text-mist text-xs">{letter.type}</div>
                  </div>
                  <StatusBadge status={letter.status} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Evolutions */}
      {evolutions.length > 0 && (
        <div className="bg-night border border-stone/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-base text-parchment">Recent Evolutions</h3>
            <button
              onClick={() => onNavigate('history')}
              className="text-rune hover:underline text-sm flex items-center gap-1"
            >
              View All <ChevronRight size={14} />
            </button>
          </div>
          <div className="space-y-2">
            {evolutions.slice(0, 3).map((evolution) => (
              <div key={evolution.id} className="flex items-center justify-between p-2 rounded-lg bg-void/50">
                <div>
                  <div className="text-parchment text-sm">v{evolution.worldStateVersion}</div>
                  <div className="text-mist text-xs">{formatDate(evolution.createdAt)}</div>
                </div>
                {evolution.letterOut && (
                  <Link
                    to={`/admin/letters-out/${evolution.letterOut.id}`}
                    className="text-rune text-xs hover:underline"
                  >
                    {evolution.letterOut.subject}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="bg-night border border-stone/30 rounded-xl p-4">
        <h3 className="font-display text-base text-parchment mb-3">Details</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-mist text-xs mb-1">World ID</div>
            <div className="text-parchment font-mono text-xs truncate" title={world.id}>
              {world.id}
            </div>
          </div>
          <div>
            <div className="text-mist text-xs mb-1">Token</div>
            <div className="text-parchment font-mono text-xs">{world.inboundToken}</div>
          </div>
          <div>
            <div className="text-mist text-xs mb-1">Address Mode</div>
            <div className="text-parchment text-xs">{world.addressMode}</div>
          </div>
          <div>
            <div className="text-mist text-xs mb-1">Auto Review</div>
            <div className="text-parchment text-xs">{world.autoReviewEnabled ? 'Enabled' : 'Disabled'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  sublabel,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
  sublabel?: string;
}) {
  return (
    <div className="bg-night border border-stone/30 rounded-xl p-4">
      <div className="flex items-center gap-2 text-mist mb-2">
        <Icon size={16} className="opacity-70" />
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-display text-parchment">{value}</div>
      {sublabel && <div className="text-xs text-mist/60 mt-1">{sublabel}</div>}
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-void/50 rounded-lg p-3">
      <div className="text-mist text-xs mb-1">{label}</div>
      <div className="text-parchment text-lg">{value}</div>
    </div>
  );
}

function NavCard({
  icon: Icon,
  title,
  description,
  onClick,
  disabled,
}: {
  icon: typeof Mail;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`text-left bg-night border border-stone/30 rounded-xl p-4 transition-all ${
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:border-rune/30 hover:bg-rune/5'
      }`}
    >
      <Icon size={20} className="text-rune mb-2" />
      <div className="font-display text-parchment mb-1">{title}</div>
      <div className="text-mist text-xs">{description}</div>
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    processed: 'bg-success/20 text-success',
    sent: 'bg-success/20 text-success',
    rejected: 'bg-ember/20 text-ember',
    failed: 'bg-ember/20 text-ember',
    pending: 'bg-stone/30 text-mist',
    queued: 'bg-stone/30 text-mist',
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs ${colors[status] ?? 'bg-stone/30 text-mist'}`}>
      {status}
    </span>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}

function formatDateShort(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
